/**
 * Lightweight telemetry client — fire-and-forget event logging.
 *
 * - Never throws. Never blocks UI.
 * - Uses `keepalive: true` so events survive page unload.
 * - Sends to backend POST /telemetry/event → MongoDB `telemetry` collection.
 */

import { API_BASE_URL } from "@/lib/config";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type EventType =
  | "app.visibility"
  | "app.network"
  // ── Crash reporting (July 2026): React error boundaries. Fired from
  // app/error.tsx and app/global-error.tsx before any auto-reload, so every
  // client-side crash is visible in Mongo instead of only on the user's
  // screen. is_chunk_load_error distinguishes stale-deploy tabs (auto-healed
  // by reload) from real render bugs.
  | "app.crash"
  | "auth.modal_open"
  | "auth.modal_close"
  | "auth.google_click"
  | "auth.email_submit"
  | "auth.success"
  | "auth.error"
  | "auth.verify_redirect"
  | "auth.verify_error"
  | "auth.password_reset_submit"
  | "auth.password_reset_success"
  | "auth.password_reset_error"
  | "auth.sign_out"
  | "billing.pricing_view"
  | "billing.trial_eligibility"
  | "billing.plan_click"
  | "billing.trial_click"
  | "billing.confirmed"
  | "billing.declaration_error"
  | "billing.checkout_start"
  | "billing.checkout_redirect"
  | "billing.checkout_error"
  | "billing.quick_upgrade_success"
  | "billing.quick_upgrade_error"
  | "billing.portal_fallback"
  | "chat.input_submit"
  | "chat.input_blocked"
  | "chat.stop_click"
  | "chat.stop_success"
  | "chat.stop_error"
  | "chat.tier_change"
  | "chat.timeline_opened"
  | "chat.timeline_jump"
  | "skill.launch_submit"
  | "skill.picked_in_composer"
  | "feedback.page_view"
  | "feedback.list_loaded"
  | "feedback.list_error"
  | "feedback.sidebar_click"
  | "feedback.create_open"
  | "feedback.create_success"
  | "feedback.create_error"
  | "feedback.detail_open"
  | "feedback.vote_click"
  | "feedback.vote_success"
  | "feedback.vote_error"
  | "feedback.answer_success"
  | "feedback.answer_error"
  | "library.sidebar_click"
  | "updates.page_view"
  | "updates.list_loaded"
  | "updates.list_error"
  | "updates.sidebar_click"
  | "session.sidebar_loaded"
  | "session.sidebar_fetch_error"
  | "session.open"
  | "session.delete_click"
  | "session.delete_success"
  | "session.delete_error"
  | "session.rename_click"
  | "session.rename_success"
  | "session.rename_error"
  | "session.favorite_toggle"
  | "session.favorite_success"
  | "session.favorite_error"
  | "session.fork_click"
  | "session.fork_success"
  | "session.fork_error"
  | "tool.card_rendered"
  // ── Inline sub-agent progress (July 2026): SEC/fund agent tree shown
  // inside the add_cell loading card so chat-only users see live progress.
  | "inline_progress.shown"
  | "inline_progress.stale_filtered"
  | "inline_progress.fetch_error"
  | "tool.generic_error_shown"
  | "tool.unsupported_dropped"
  | "artifact.open"
  | "artifact.canvas_fetch_ok"
  | "artifact.canvas_fetch_error"
  | "artifact.canvas_cells"
  | "artifact.snapshot_fetch_ok"
  | "artifact.snapshot_fetch_failed"
  | "artifact.canvas_ready"
  | "alert_canvas.layout_state"
  | "alert_canvas.bad_state_detected"
  | "stream.start"
  | "stream.complete"
  | "stream.error"
  // ── Metric reclassification (Aug 2026): stream.error was inflated ~2x. ──
  // limit_hit: billing/usage walls (credits exhausted, trial ended, daily
  //   Gemini cap). Expected product behavior, NOT a failure.
  // interrupted: connection died mid-stream but the drop was routed into the
  //   silent recovery pipeline; the user usually still gets the answer.
  // recovered: a live_drop recovery completed successfully (pairs with
  //   interrupted so failure rate = error / start, no joins needed).
  // After this split, stream.error = genuinely user-visible dead ends only.
  | "stream.limit_hit"
  | "stream.interrupted"
  | "stream.recovered"
  | "stream.abort"
  | "stream.drop"
  | "stream.visibility"
  | "stream.network"
  | "stream.resume_start"
  | "stream.resume_complete"
  | "stream.resume_status"
  | "stream.resume_decision"
  | "stream.resume_fallback"
  | "stream.poll_start"
  | "stream.poll_progress"
  | "stream.poll_complete"
  | "stream.error_routed_to_recovery"
  | "stream.recovery_trigger"
  | "stream.recovery_started"
  | "stream.recovery_gated"
  | "stream.recovery_complete"
  | "stream.recovery_cancelled"
  | "stream.recovery_failed_shown"
  | "stream.manual_reload"
  // ── Reliability (July 2026): failed_to_start silent retry ──
  // Emitted when a stream dies with ZERO chunks received while the device is
  // online (mobile radio blip / request never reached the backend). We
  // re-submit once silently instead of showing the error card. outcome:
  // "attempt" when firing the retry, "gave_up" when the retry itself failed
  // and we fall through to the visible error card.
  | "stream.auto_retry_submit"
  // ── Reliability v2 (May 2026): false-drop suppression + auto re-hydrate ──
  // Emitted by the drop watchdog when a >30s "drop" is ignored because the
  // tab was backgrounded (SSE reader throttled → buffered heartbeats), and by
  // the recovery pipeline when it silently re-loads the saved answer from the
  // DB instead of showing the manual reload button. All tagged
  // recovery_version:"v2" for before/after comparison vs v1.
  | "stream.drop_suppressed_hidden"
  // ── Reliability v3 (June 2026): authoritative backend status probe ──
  // Emitted when the frontend asks /agent/request-status whether a quiet
  // stream is actually still running server-side (immune to the activity
  // clock resets that blinded the v2 watchdog under rapid tab switching).
  // Tagged recovery_version:"v3".
  | "stream.backend_status_check"
  | "stream.auto_rehydrate_attempt"
  | "stream.auto_rehydrate_success"
  | "stream.auto_rehydrate_exhausted"
  | "stream.poll_timeout"
  | "stream.beforeunload"
  | "stream.summary"
  | "reasoning.display_deduped"
  | "alert.fork_click"
  | "alert.create_click"
  | "alert.edit_card_open"
  | "alert.edit_card_blocked"
  | "alert.fork_card_open"
  | "alert.fork_card_dismiss"
  | "alert.fork_card_blocked"
  | "alert.create_card_open"
  | "alert.create_card_dismiss"
  | "alert.create_card_blocked"
  // ── Subscribe instant-refresh + sidebar entrance animation (June 2026) ──
  // The subscribe path optimistically inserts the row into the sidebar's
  // `alerts:subscribed` SWR cache (instant pop + section reveal, no second
  // network round-trip) then revalidates. The sidebar plays a pop+shake on
  // the newly-added subscribed row only. These events trace that flow so we
  // can confirm in MongoDB that the optimistic insert + animation fired.
  | "alert.subscribe_click"
  | "alert.subscribe_success"
  | "alert.subscribe_error"
  | "alert.unsubscribe_success"
  | "alert.subscribe_sidebar_animated"
  // ── Activation funnel: questionnaire → replay → finale → trial ───────
  // The "tour" starts the moment the post-signup questionnaire opens
  // and ends when the user clicks "Begin your trial now" in the finale
  // modal. Every meaningful step in between is its own event so we can
  // build a multi-stage funnel in MongoDB aggregations and find drop-off
  // points without log mining. See app/api/routes/replay.py and
  // demofiles/buddy_scripts/market_edge_scan_setups.json for the 38-beat
  // tour script that drives `replay.beat_*` events.
  | "onboarding.questionnaire_open"
  | "onboarding.questionnaire_stage_completed"
  | "onboarding.questionnaire_dismissed"
  | "onboarding.questionnaire_completed"
  | "onboarding.demo_replay_route"
  | "replay.started"
  | "replay.beat_reached"
  | "replay.beat_released"
  | "replay.publish_gate_shown"
  | "replay.publish_gate_resumed"
  | "replay.handoff_to_chat"
  | "replay.completed"
  | "replay.skipped"
  | "replay.exited"
  | "replay.paused"
  | "replay.resumed"
  | "replay.speed_changed"
  | "replay.ask_user_answered"
  | "onboarding.finale_modal_open"
  | "onboarding.finale_step_advance"
  | "onboarding.finale_trial_click"
  | "onboarding.finale_dismissed"
  | "onboarding.video_open"
  | "onboarding.video_dismissed";

interface TelemetryBaseParams {
  session_id?: string;
  user_id?: string;
  request_id?: string;
  [key: string]: unknown;
}

interface StreamStartParams extends TelemetryBaseParams {}

interface StreamCompleteParams extends TelemetryBaseParams {
  duration_ms: number;
  chunks_received?: number;
}

interface StreamErrorParams extends TelemetryBaseParams {
  error_type: string;
  error_subtype?: string;
  error_message: string;
  /** Raw underlying error (name + message) for diagnosis; error_message may be canned. */
  error_raw?: string;
  duration_ms?: number;
}

interface StreamAbortParams extends TelemetryBaseParams {
  duration_ms: number;
}

interface StreamDropParams extends TelemetryBaseParams {
  duration_ms: number;
  seconds_since_last_chunk: number;
  chunks_received: number;
  visibility_state: string;
  is_online: boolean;
}

interface StreamVisibilityParams extends TelemetryBaseParams {
  visibility_state: string;
  stream_active: boolean;
  duration_ms?: number;
}

interface StreamNetworkParams extends TelemetryBaseParams {
  is_online: boolean;
  stream_active: boolean;
  duration_ms?: number;
}

interface StreamResumeStartParams extends TelemetryBaseParams {
  request_id: string;
  cursor: string | null;
  trigger: "mount" | "visibility" | "manual" | "live_drop";
}

interface StreamResumeCompleteParams extends TelemetryBaseParams {
  request_id: string;
  duration_ms: number;
  chunks_processed: number;
  chunks_deduped: number;
  outcome: "ok" | "error" | "empty";
}

// ---------------------------------------------------------------------------
// Device fingerprint — collected once, attached to every event
// ---------------------------------------------------------------------------

function getDeviceInfo(): Record<string, unknown> {
  if (typeof window === "undefined") return {};
  try {
    const nav = navigator as any;
    const conn = nav.connection || nav.mozConnection || nav.webkitConnection;
    const ua = nav.userAgent || "";
    const isMobile = /mobile|android|iphone|ipad/i.test(ua);
    const isIOS = /iphone|ipad|ipod/i.test(ua);
    const isAndroid = /android/i.test(ua);
    let browser = "unknown";
    if (/edg/i.test(ua)) browser = "edge";
    else if (/opr|opera/i.test(ua)) browser = "opera";
    else if (/firefox/i.test(ua)) browser = "firefox";
    else if (/chrome/i.test(ua)) browser = "chrome";
    else if (/safari/i.test(ua)) browser = "safari";

    return {
      device_is_mobile: isMobile,
      device_is_ios: isIOS,
      device_is_android: isAndroid,
      device_browser: browser,
      device_ua: ua.slice(0, 300),
      device_language: nav.language || undefined,
      device_platform: nav.platform || undefined,
      device_screen: `${screen.width}x${screen.height}`,
      device_pixel_ratio: window.devicePixelRatio,
      device_max_touch: nav.maxTouchPoints ?? undefined,
      net_type: conn?.type || undefined,
      net_effective_type: conn?.effectiveType || undefined,
      net_downlink: conn?.downlink || undefined,
      net_rtt: conn?.rtt || undefined,
    };
  } catch {
    return {};
  }
}

let _cachedDevice: Record<string, unknown> | null = null;
function deviceInfo(): Record<string, unknown> {
  if (!_cachedDevice) _cachedDevice = getDeviceInfo();
  return _cachedDevice;
}

// ---------------------------------------------------------------------------
// Transport
// ---------------------------------------------------------------------------

function send(
  event_type: EventType,
  data: Record<string, unknown>,
): void {
  try {
    fetch(`${API_BASE_URL}/telemetry/event`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        event_type,
        ...deviceInfo(),
        ...data,
        // Backend schema requires session_id. Site-level events use "global".
        session_id:
          typeof data.session_id === "string" && data.session_id.length > 0
            ? data.session_id
            : "global",
        timestamp: new Date().toISOString(),
        page: typeof window !== "undefined" ? window.location.pathname : undefined,
        visibility_state: typeof document !== "undefined" ? document.visibilityState : undefined,
        is_online: typeof navigator !== "undefined" ? navigator.onLine : undefined,
      }),
      keepalive: true,
      credentials: "include",
    }).catch(() => {});
  } catch {
    // Telemetry must never throw
  }
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export const telemetry = {
  /** Generic fire-and-forget emitter for new event types. */
  emit(eventType: EventType, params: TelemetryBaseParams): void {
    send(eventType, params as Record<string, unknown>);
  },

  /** User submitted a message — stream starting. */
  streamStart(params: StreamStartParams): void {
    send("stream.start", params as unknown as Record<string, unknown>);
  },

  /** Stream completed successfully. */
  streamComplete(params: StreamCompleteParams): void {
    send("stream.complete", params as unknown as Record<string, unknown>);
  },

  /** Stream ended with an error (error card shown to user). */
  streamError(params: StreamErrorParams): void {
    send("stream.error", params as unknown as Record<string, unknown>);
  },

  /** User aborted the stream (clicked stop). */
  streamAbort(params: StreamAbortParams): void {
    send("stream.abort", params as unknown as Record<string, unknown>);
  },

  /** Stream went silent — no chunks for 30s while status still "streaming". */
  streamDrop(params: StreamDropParams): void {
    send("stream.drop", params as unknown as Record<string, unknown>);
  },

  /** Tab visibility changed during an active stream. */
  streamVisibility(params: StreamVisibilityParams): void {
    send("stream.visibility", params as unknown as Record<string, unknown>);
  },

  /** Network online/offline changed during an active stream. */
  streamNetwork(params: StreamNetworkParams): void {
    send("stream.network", params as unknown as Record<string, unknown>);
  },

  /** Stream resume initiated (reconnecting to in-progress agent). */
  streamResumeStart(params: StreamResumeStartParams): void {
    send("stream.resume_start", params as unknown as Record<string, unknown>);
  },

  /** Stream resume finished — includes dedup stats. */
  streamResumeComplete(params: StreamResumeCompleteParams): void {
    send("stream.resume_complete", params as unknown as Record<string, unknown>);
  },
};
